/**
 * Experience Badge Configuration
 * Provides icon and label configuration for experience types
 * Follows Single Responsibility Principle - only handles badge data
 */

import React from 'react';
import { getExperienceTypeLabel } from '@/utils/experienceLabels';

export type ExperienceType =
  | 'activities'
  | 'package'
  | 'riders-corner'
  | 'signature'
  | 'stays';

export interface BadgeConfig {
  label: string;
  icon: React.ReactNode;
}

/**
 * Get badge configuration for a given experience type
 * Open/Closed Principle - can be extended with new types without modifying existing code
 */
export const getExperienceBadgeConfig = (type: ExperienceType): BadgeConfig => {
  switch (type) {
    case 'activities':
      return {
        label: getExperienceTypeLabel('activities'),
        icon: (
          <svg
            width='16'
            height='16'
            viewBox='0 0 20 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M10 2.5L12.5 7.5L10 12.5L7.5 7.5L10 2.5Z'
              stroke='white'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M7.5 7.5L10 12.5L12.5 7.5M10 12.5V17.5'
              stroke='white'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        ),
      };
    case 'package':
      return {
        label: getExperienceTypeLabel('package'),
        icon: (
          <svg
            width='16'
            height='16'
            viewBox='0 0 20 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M13.3333 10.75V14.25C13.3333 15.2145 12.5478 16 11.5833 16H5.75C4.78547 16 4 15.2145 4 14.25V8.41667C4 7.45214 4.78547 6.66667 5.75 6.66667H9.25'
              stroke='white'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M13.3333 10.75H10.5833C9.61881 10.75 8.83334 9.96453 8.83334 9V6.25C8.83334 5.28547 9.61881 4.5 10.5833 4.5H14.25C15.2145 4.5 16 5.28547 16 6.25V9C16 9.96453 15.2145 10.75 14.25 10.75H13.3333Z'
              fill='white'
              stroke='white'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        ),
      };
    case 'riders-corner':
      return {
        label: getExperienceTypeLabel('riders-corner'),
        icon: (
          <svg
            width='16'
            height='16'
            viewBox='0 0 20 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M2.5 6.66667C2.5 5.74619 3.24619 5 4.16667 5H15.8333C16.7538 5 17.5 5.74619 17.5 6.66667V13.3333C17.5 14.2538 16.7538 15 15.8333 15H4.16667C3.24619 15 2.5 14.2538 2.5 13.3333V6.66667Z'
              stroke='white'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M5 15V17.5M15 15V17.5M2.5 10H17.5'
              stroke='white'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        ),
      };
    case 'signature':
      return {
        label: getExperienceTypeLabel('signature'),
        icon: (
          <svg
            width='16'
            height='16'
            viewBox='0 0 20 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z'
              stroke='white'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M10 6.66667V10L13.3333 13.3333'
              stroke='white'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        ),
      };
    case 'stays':
      return {
        label: getExperienceTypeLabel('stays'),
        icon: (
          <svg
            width='16'
            height='16'
            viewBox='0 0 20 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M2.5 8.33333L10 3.33333L17.5 8.33333V16.6667C17.5 17.5871 16.7538 18.3333 15.8333 18.3333H4.16667C3.24619 18.3333 2.5 17.5871 2.5 16.6667V8.33333Z'
              stroke='white'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M7.5 18.3333V10H12.5V18.3333'
              stroke='white'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        ),
      };
    default:
      return {
        label: 'Experience',
        icon: null,
      };
  }
};
